import { useMutation, useQuery } from "react-query";
import { AxiosError, AxiosResponse } from "axios";
import { config } from "../config";
import {
  ApiError,
  CreateJobPayload,
  JobResponse,
  QueryOptions,
  UpdateJobPayload,
} from "../types";
import { RecurrenceEditScope } from "../enums";
import { client } from "./axios";
import { DEFAULT_QUERY_OPTIONS } from "../constants";

export const useFetchJobsQuery = (
  orgId: number,
  startDate: string,
  endDate: string,
  queryOptions?: QueryOptions<JobResponse[]>,
) => {
  const fetchJobs = async (): Promise<JobResponse[]> => {
    const path = config.api.scheduleService.getJobs
      .replace("{orgId}", orgId.toString())
      .replace("{startDate}", encodeURIComponent(startDate))
      .replace("{endDate}", encodeURIComponent(endDate));
    const response = await client.get<{ response: JobResponse[] }>(path);
    return response.data.response;
  };

  return useQuery(["fetchJobs", orgId, startDate, endDate], fetchJobs, {
    ...DEFAULT_QUERY_OPTIONS,
    ...queryOptions,
  });
};

export const useCreateJobMutation = () => {
  const mutation = useMutation<
    AxiosResponse,
    AxiosError<ApiError>,
    { orgId: number } & CreateJobPayload,
    unknown
  >(async (data: { orgId: number } & CreateJobPayload) => {
    const { orgId, ...job } = data;
    const path = config.api.scheduleService.createJob.replace(
      "{orgId}",
      orgId.toString(),
    );

    const response = await client.post(path, job);
    return response;
  });

  return mutation;
};

export const useUpdateJobMutation = () => {
  const mutation = useMutation<
    AxiosResponse,
    AxiosError<ApiError>,
    { orgId: number; id: number } & UpdateJobPayload,
    unknown
  >(async (data: { orgId: number; id: number } & UpdateJobPayload) => {
    const { orgId, id, ...job } = data;
    const path = config.api.scheduleService.updateJob
      .replace("{orgId}", orgId.toString())
      .replace("{id}", id.toString());

    const response = await client.patch(path, job);
    return response;
  });

  return mutation;
};

export const useDeleteJobMutation = () => {
  const mutation = useMutation<
    AxiosResponse,
    AxiosError<ApiError>,
    { orgId: number; id: number; scope: RecurrenceEditScope },
    unknown
  >(async (data: { orgId: number; id: number; scope: RecurrenceEditScope }) => {
    const path = config.api.scheduleService.deleteJob
      .replace("{orgId}", data.orgId.toString())
      .replace("{id}", data.id.toString())
      .replace("{scope}", data.scope);

    const response = await client.delete(path);
    return response;
  });

  return mutation;
};
